import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import Stage0Toggle from '@/components/admin/Stage0Toggle';
import EvidenceTab from '@/components/admin/EvidenceTab';
import { 
  Loader2, LogOut, Search, Download, RefreshCw, 
  FileText, Users, CreditCard, HelpCircle, Eye,
  RotateCcw, UserX, Mail, BarChart3, Edit, Plus,
  Save, X, Printer, Link2, AlertTriangle, CheckCircle,
  ClipboardList, Copy, UserPlus, Shield, Key, Trash2,
  Megaphone, BookOpen, Cpu
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_readiness-check-27/artifacts/el8npnpt_CXOlogo.png";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { adminLogout, getAdminToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('assessments');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Data states
  const [stats, setStats] = useState({});
  const [assessments, setAssessments] = useState([]);
  const [users, setUsers] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [pillars, setPillars] = useState([]);
  const [payments, setPayments] = useState([]);
  const [piiLeads, setPiiLeads] = useState([]);
  const [llmUsageSummary, setLlmUsageSummary] = useState([]);
  
  // Modal states
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [assessmentDetails, setAssessmentDetails] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Question editing states
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [questionForm, setQuestionForm] = useState({});
  
  // Report viewer state
  const [selectedReportAssessment, setSelectedReportAssessment] = useState(null);
  const [reportDetails, setReportDetails] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');

  // HTML Preview modal state
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Create User state
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [createUserForm, setCreateUserForm] = useState({
    first_name: '', last_name: '', work_email: '', org_name: '',
    phone: '', industry: '', org_size: '', designation: '', payment_received: false
  });
  const [createdCredentials, setCreatedCredentials] = useState(null);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const reportPollRef = useRef({ interval: null, timeout: null });
  const [sdfFilter, setSdfFilter] = useState('all');
  const [adminUsers, setAdminUsers] = useState([]);
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [newAdminUsername, setNewAdminUsername] = useState('');
  const [newAdminDisplayName, setNewAdminDisplayName] = useState('');
  const [createdAdminCreds, setCreatedAdminCreds] = useState(null);
  const [accessRequests, setAccessRequests] = useState([]);
  const token = getAdminToken();
  const authHeaders = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchAllData();
    return () => {
      // Stop any in-flight report poller on unmount.
      if (reportPollRef.current.interval) clearInterval(reportPollRef.current.interval);
      if (reportPollRef.current.timeout) clearTimeout(reportPollRef.current.timeout);
    };
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [statsRes, assessmentsRes, usersRes, questionsRes, paymentsRes, adminUsersRes, accessRequestsRes, leadsRes, llmUsageRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard-stats`, { headers: authHeaders }),
        axios.get(`${API}/admin/assessments`, { headers: authHeaders }),
        axios.get(`${API}/admin/users`, { headers: authHeaders }),
        axios.get(`${API}/admin/questions`, { headers: authHeaders }),
        axios.get(`${API}/admin/payments`, { headers: authHeaders }),
        axios.get(`${API}/admin/admin-users`, { headers: authHeaders }),
        axios.get(`${API}/admin/access-requests`, { headers: authHeaders }),
        axios.get(`${API}/admin/pii-leads`, { headers: authHeaders }),
        axios.get(`${API}/admin/llm-usage/summary`, { headers: authHeaders })
      ]);
      
      setStats(statsRes.data);
      setAssessments(assessmentsRes.data.assessments);
      setUsers(usersRes.data.users);
      setQuestions(questionsRes.data.questions);
      setPillars(questionsRes.data.pillars);
      setPayments(paymentsRes.data.payments);
      setAdminUsers(adminUsersRes.data.admin_users);
      setAccessRequests(accessRequestsRes.data.access_requests);
      setPiiLeads(leadsRes.data.leads || []);
      setLlmUsageSummary(llmUsageRes.data.models || []);
    } catch (error) {
      toast.error('Failed to load dashboard data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    adminLogout();
    navigate('/admin');
  };

  // ==================== ASSESSMENT & REPORT FUNCTIONS ====================

  const viewAssessmentReport = async (assessment) => {
    setSelectedAssessment(assessment);
    setShowReportModal(true);
    
    try {
      const response = await axios.get(
        `${API}/admin/assessment/${assessment.id}`,
        { headers: authHeaders }
      );
      setAssessmentDetails(response.data);
    } catch (error) {
      toast.error('Failed to load assessment details');
    }
  };

  const openReportViewer = async (assessment) => {
    setSelectedReportAssessment(assessment);
    setActiveTab('report-viewer');
    
    try {
      const response = await axios.get(
        `${API}/admin/assessment/${assessment.id}`,
        { headers: authHeaders }
      );
      setReportDetails(response.data);
      setAdminNotes(response.data.report?.admin_notes || '');
    } catch (error) {
      toast.error('Failed to load report details');
    }
  };

  const updateReportStatus = async (assessmentId, field, value) => {
    try {
      await axios.put(
        `${API}/admin/report/${assessmentId}`,
        { [field]: value },
        { headers: authHeaders }
      );
      toast.success('Status updated');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const saveAdminNotes = async () => {
    if (!selectedReportAssessment) return;
    try {
      await axios.put(
        `${API}/admin/report/${selectedReportAssessment.id}`,
        { admin_notes: adminNotes },
        { headers: authHeaders }
      );
      toast.success('Notes saved');
    } catch (error) {
      toast.error('Failed to save notes');
    }
  };

  const openReportPreview = async () => {
    if (!selectedReportAssessment) return;
    if (!reportDetails?.report?.has_docx) {
      toast.error('Generate the DOCX report first before previewing.');
      return;
    }
    setLoadingPreview(true);
    setShowPreviewModal(true);
    try {
      const res = await axios.get(
        `${API}/admin/report/${selectedReportAssessment.id}/html-preview`,
        { headers: authHeaders }
      );
      setPreviewHtml(res.data.html || '');
      // Auto-scroll to TOC after render
      setTimeout(() => {
        const scrollable = document.querySelector('[data-testid="report-preview-modal"] .overflow-y-auto');
        if (!scrollable) return;
        const content = scrollable.querySelector('[data-testid="preview-html-content"]');
        if (!content) return;
        const walker = document.createTreeWalker(content, NodeFilter.SHOW_TEXT);
        let node;
        while ((node = walker.nextNode())) {
          if (node.textContent.includes('Table of Contents') || node.textContent.includes('Section E')) {
            node.parentElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            break;
          }
        }
      }, 300);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load preview');
      setShowPreviewModal(false);
    } finally {
      setLoadingPreview(false);
    }
  };

  const copyShareableLink = () => {
    const link = `${window.location.origin}/report/${selectedReportAssessment?.id}`;
    navigator.clipboard.writeText(link);
    toast.success('Link copied to clipboard');
  };

  const printReport = () => {
    window.print();
  };

  const generateDocxReport = async () => {
    if (!selectedReportAssessment) return;
    setGeneratingReport(true);

    // Clear any stale poller from a previous run.
    if (reportPollRef.current.interval) clearInterval(reportPollRef.current.interval);
    if (reportPollRef.current.timeout) clearTimeout(reportPollRef.current.timeout);

    const assessmentId = selectedReportAssessment.id;

    const refreshReportDetails = async () => {
      const response = await axios.get(`${API}/admin/assessment/${assessmentId}`, { headers: authHeaders });
      setReportDetails(response.data);
    };

    const stopPolling = () => {
      if (reportPollRef.current.interval) clearInterval(reportPollRef.current.interval);
      if (reportPollRef.current.timeout) clearTimeout(reportPollRef.current.timeout);
      reportPollRef.current = { interval: null, timeout: null };
    };

    try {
      const res = await axios.post(
        `${API}/admin/report/${assessmentId}/generate`,
        {},
        { headers: authHeaders }
      );
      if (res.data.status === 'generating') {
        toast.info('AI-enriched report generation started. This usually takes 2-4 minutes...');
        // Poll for completion every 5s. The report is generated by a
        // background task, so it completes regardless of this poller.
        reportPollRef.current.interval = setInterval(async () => {
          try {
            const statusRes = await axios.get(
              `${API}/admin/report/${assessmentId}/generation-status`,
              { headers: authHeaders }
            );
            if (statusRes.data.status === 'completed') {
              stopPolling();
              await refreshReportDetails();
              setGeneratingReport(false);
              toast.success('DOCX report generated successfully!');
            } else if (statusRes.data.status === 'failed') {
              stopPolling();
              setGeneratingReport(false);
              toast.error('Report generation failed: ' + (statusRes.data.error || 'Unknown error'));
            }
          } catch (e) {
            // Transient polling error — keep polling.
          }
        }, 5000);

        // Safety timeout after 8 minutes. Before giving up, do ONE final
        // status check so a just-completed report is still surfaced.
        reportPollRef.current.timeout = setTimeout(async () => {
          stopPolling();
          try {
            const statusRes = await axios.get(
              `${API}/admin/report/${assessmentId}/generation-status`,
              { headers: authHeaders }
            );
            if (statusRes.data.status === 'completed') {
              await refreshReportDetails();
              toast.success('DOCX report generated successfully!');
            } else {
              toast.info('Report is still generating in the background. Click "Generate DOCX" again in a minute to check.');
            }
          } catch (e) {
            toast.info('Report is still generating in the background. Refresh to check.');
          }
          setGeneratingReport(false);
        }, 660000);
      } else {
        toast.success('Report generated');
        await refreshReportDetails();
        setGeneratingReport(false);
      }
    } catch (err) {
      stopPolling();
      toast.error(err.response?.data?.detail || 'Failed to start report generation');
      setGeneratingReport(false);
    }
  };

  const downloadDocxReport = async () => {
    if (!selectedReportAssessment) return;
    setDownloadingReport(true);
    try {
      const response = await axios.get(
        `${API}/admin/report/${selectedReportAssessment.id}/download`,
        { headers: authHeaders, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
        : `DPDP_Report_${selectedReportAssessment.id}.docx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    } catch (err) {
      toast.error(err.response?.status === 404 ? 'Generate the report first before downloading' : 'Failed to download report');
    } finally {
      setDownloadingReport(false);
    }
  };

  // ==================== USER MANAGEMENT FUNCTIONS ====================

  const resendCredentials = async (userId) => {
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/admin/user/${userId}/resend-credentials`,
        {},
        { headers: authHeaders }
      );
      toast.success('Credentials resent');
    } catch (error) {
      toast.error('Failed to resend credentials');
    } finally {
      setActionLoading(false);
    }
  };

  const resetAssessment = async (userId) => {
    if (!window.confirm('Are you sure you want to reset this user\'s assessment? This cannot be undone.')) return;
    
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/admin/user/${userId}/reset-assessment`,
        {},
        { headers: authHeaders }
      );
      toast.success('Assessment reset');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to reset assessment');
    } finally {
      setActionLoading(false);
    }
  };

  const revokeAccess = async (userId) => {
    if (!window.confirm('Are you sure you want to revoke access for this user?')) return;
    
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/admin/user/${userId}/revoke-access`,
        {},
        { headers: authHeaders }
      );
      toast.success('Access revoked');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to revoke access');
    } finally {
      setActionLoading(false);
    }
  };

  // ==================== USER CREATION FUNCTIONS ====================

  const handleCreateUserFormChange = (field, value) => {
    setCreateUserForm(prev => ({ ...prev, [field]: value }));
  };

  const openCreateUser = () => {
    setCreateUserForm({
      first_name: '', last_name: '', work_email: '', org_name: '',
      phone: '', industry: '', org_size: '', designation: '', payment_received: false
    });
    setShowCreateUserModal(true);
  };

  const submitCreateUser = async () => {
    if (!createUserForm.first_name || !createUserForm.last_name || !createUserForm.work_email || !createUserForm.org_name) {
      toast.error('Please fill in all required fields');
      return;
    }
    setActionLoading(true);
    try {
      const response = await axios.post(
        `${API}/admin/create-user`,
        createUserForm,
        { headers: authHeaders }
      );
      setShowCreateUserModal(false);
      setCreatedCredentials(response.data);
      setShowCredentialsModal(true);
      fetchAllData();
      toast.success('User created successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setActionLoading(false);
    }
  };

  const copyCredentials = () => {
    if (!createdCredentials) return;
    const text = `Username: ${createdCredentials.display_username}\nPassword: ${createdCredentials.temp_password}`;
    navigator.clipboard.writeText(text);
    toast.success('Credentials copied to clipboard');
  };

  // ==================== ADMIN USER MANAGEMENT FUNCTIONS ====================

  const handleCreateAdmin = async () => {
    if (!newAdminUsername.trim()) {
      toast.error('Please enter a username');
      return;
    }
    setActionLoading(true);
    try {
      const response = await axios.post(
        `${API}/admin/admin-users`,
        { username: newAdminUsername.trim(), display_name: newAdminDisplayName.trim() },
        { headers: authHeaders }
      );
      setShowCreateAdmin(false);
      setNewAdminUsername('');
      setNewAdminDisplayName('');
      setCreatedAdminCreds(response.data.admin_user);
      fetchAllData();
      toast.success(`Admin user ${response.data.admin_user.username} created`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create admin user');
    } finally {
      setActionLoading(false);
    }
  };

  const resetAdminPassword = async (adminId) => {
    if (!window.confirm('Reset password for this admin user?')) return;
    setActionLoading(true);
    try {
      const response = await axios.put(
        `${API}/admin/admin-users/${adminId}/reset-password`,
        {},
        { headers: authHeaders }
      );
      setCreatedAdminCreds({ username: response.data.username, temp_password: response.data.new_password });
      toast.success('Password reset successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setActionLoading(false);
    }
  };

  const toggleAdminStatus = async (adminId) => {
    setActionLoading(true);
    try {
      const response = await axios.put(
        `${API}/admin/admin-users/${adminId}/toggle-status`,
        {},
        { headers: authHeaders }
      );
      fetchAllData();
      toast.success(response.data.is_active ? 'Admin activated' : 'Admin deactivated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };

  const deleteAdmin = async (adminId, username) => {
    if (!window.confirm(`Delete admin user ${username}? This action cannot be undone.`)) return;
    setActionLoading(true);
    try {
      await axios.delete(`${API}/admin/admin-users/${adminId}`, { headers: authHeaders });
      fetchAllData();
      toast.success(`Admin user ${username} deleted`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete admin user');
    } finally {
      setActionLoading(false);
    }
  };

  const copyAdminCreds = () => {
    if (!createdAdminCreds) return;
    const text = `Login URL: ${window.location.origin}/admin\nUsername: ${createdAdminCreds.username}\nPassword: ${createdAdminCreds.temp_password}`;
    navigator.clipboard.writeText(text);
    toast.success('Credentials copied to clipboard');
  };

  // ==================== ACCESS REQUEST MANAGEMENT ====================

  const approveAccessRequest = async (requestId) => {
    setActionLoading(true);
    try {
      await axios.put(`${API}/admin/access-requests/${requestId}/approve`, {}, { headers: authHeaders });
      fetchAllData();
      toast.success('Access request approved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    } finally { setActionLoading(false); }
  };

  const rejectAccessRequest = async (requestId) => {
    if (!window.confirm('Reject this access request?')) return;
    setActionLoading(true);
    try {
      await axios.put(`${API}/admin/access-requests/${requestId}/reject`, {}, { headers: authHeaders });
      fetchAllData();
      toast.success('Access request rejected');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    } finally { setActionLoading(false); }
  };

  // ==================== PII LEADS FUNCTIONS ====================

  const [leadsIndustryFilter, setLeadsIndustryFilter] = useState('all');
  const [leadsGapFilter, setLeadsGapFilter] = useState('all');

  const filteredLeads = piiLeads.filter(lead => {
    const matchSearch = !searchTerm || 
      lead.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.company_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchIndustry = leadsIndustryFilter === 'all' || lead.industry === leadsIndustryFilter;
    const matchGap = leadsGapFilter === 'all' || lead.visibility_gap_level === leadsGapFilter;
    return matchSearch && matchIndustry && matchGap;
  });

  const leadsIndustries = [...new Set(piiLeads.map(l => l.industry).filter(Boolean))];

  const exportLeadsCSV = async () => {
    try {
      const response = await axios.get(`${API}/admin/pii-leads/export-csv`, {
        headers: authHeaders,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `pii_leads_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch {
      toast.error('Failed to export CSV');
    }
  };

  // ==================== QUESTION MANAGEMENT FUNCTIONS ====================

  const openQuestionEditor = (question = null) => {
    if (question) {
      setEditingQuestion(question);
      setQuestionForm({ ...question });
    } else {
      setEditingQuestion(null);
      setQuestionForm({
        pillar_code: 'P1',
        question_number: `Q${questions.length + 1}`,
        question_text: '',
        option_a: '',
        option_b: '',
        option_c: '',
        option_d: '',
        dpdp_reference: '',
        gap_title: '',
        gap_recommendation: '',
        evidence_artefact: '',
        owner_function: '',
        phase: 1,
        effort: 'Medium',
        is_active: true
      });
    }
    setShowQuestionModal(true);
  };

  const handleQuestionFormChange = (field, value) => {
    setQuestionForm(prev => ({ ...prev, [field]: value }));
  };

  const saveQuestion = async () => {
    setActionLoading(true);
    try {
      if (editingQuestion) {
        await axios.put(
          `${API}/admin/question/${editingQuestion.id}`,
          questionForm,
          { headers: authHeaders }
        );
        toast.success('Question updated');
      } else {
        await axios.post(
          `${API}/admin/question`,
          questionForm,
          { headers: authHeaders }
        );
        toast.success('Question added');
      }
      setShowQuestionModal(false);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to save question');
    } finally {
      setActionLoading(false);
    }
  };

  const toggleQuestionActive = async (question) => {
    try {
      await axios.put(
        `${API}/admin/question/${question.id}`,
        { is_active: !question.is_active },
        { headers: authHeaders }
      );
      toast.success(`Question ${question.is_active ? 'deactivated' : 'activated'}`);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update question');
    }
  };

  // ==================== HELPER FUNCTIONS ====================

  const getRAGBadge = (rag) => {
    const colors = {
      RED: 'bg-red-100 text-red-800 border-red-300',
      AMBER: 'bg-amber-100 text-amber-800 border-amber-300',
      YELLOW: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      GREEN: 'bg-green-100 text-green-800 border-green-300',
      ORANGE: 'bg-orange-100 text-orange-800 border-orange-300',
      TEAL: 'bg-teal-100 text-teal-800 border-teal-300'
    };
    return <Badge className={`${colors[rag] || 'bg-slate-100'} border`}>{rag}</Badge>;
  };

  const getRiskBadge = (risk) => {
    const colors = {
      HIGH: 'bg-red-100 text-red-800',
      MEDIUM: 'bg-amber-100 text-amber-800',
      LOW: 'bg-green-100 text-green-800',
      RED: 'bg-red-100 text-red-800',
      ORANGE: 'bg-orange-100 text-orange-800',
      AMBER: 'bg-amber-100 text-amber-800',
      GREEN: 'bg-green-100 text-green-800',
      TEAL: 'bg-teal-100 text-teal-800'
    };
    return <Badge className={colors[risk] || 'bg-slate-100'}>{risk}</Badge>;
  };

  const getGapBadge = (tag) => {
    const colors = {
      CRITICAL: 'bg-red-100 text-red-800',
      MAJOR: 'bg-amber-100 text-amber-800',
      MINOR: 'bg-blue-100 text-blue-800',
      PASS: 'bg-green-100 text-green-800'
    };
    return <Badge className={colors[tag] || 'bg-slate-100'}>{tag}</Badge>;
  };

  const getSdfRiskText = (assessment) => {
    if (!assessment.pillar_scores_json) return null;
    try {
      const parsed = JSON.parse(assessment.pillar_scores_json);
      const p9 = parsed.pillars?.P9;
      if (assessment.sdf_flag) return 'HIGH RISK';
      if (!p9) return null;
      const rag = p9.rag;
      if (rag === 'RED') return 'PROBABLE';
      if (rag === 'ORANGE') return 'LIKELY';
      if (rag === 'AMBER') return 'POSSIBLE';
      return 'UNLIKELY';
    } catch { return null; }
  };

  const getSdfRiskBadge = (assessment) => {
    const text = getSdfRiskText(assessment);
    if (!text) return '-';
    const colors = {
      'HIGH RISK': 'bg-red-100 text-red-800 border-red-300',
      'PROBABLE': 'bg-orange-100 text-orange-800 border-orange-300',
      'LIKELY': 'bg-amber-100 text-amber-800 border-amber-300',
      'POSSIBLE': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'UNLIKELY': 'bg-green-100 text-green-800 border-green-300'
    };
    return <Badge data-testid={`sdf-risk-badge-${assessment.id}`} className={`${colors[text] || 'bg-slate-100'} border`}>{text}</Badge>;
  };

  const getPillarScore = (assessment, pillarCode) => {
    if (!assessment.pillar_scores_json) return null;
    try {
      const parsed = JSON.parse(assessment.pillar_scores_json);
      return parsed.pillars?.[pillarCode] || null;
    } catch { return null; }
  };

  const filteredAssessments = assessments.filter(a => {
    const matchSearch = a.org_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.assessment_ref_id?.toLowerCase().includes(searchTerm.toLowerCase());
    if (!matchSearch) return false;
    if (sdfFilter === 'all') return true;
    const sdfText = getSdfRiskText(a);
    return sdfText?.toUpperCase() === sdfFilter.toUpperCase();
  });

  const filteredUsers = users.filter(u =>
    u.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.org_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.work_email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredPayments = payments.filter(p =>
    p.org_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.razorpay_order_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredQuestions = questions.filter(q =>
    q.question_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    q.question_text?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    q.pillar_code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#008080] mx-auto mb-4" />
          <p className="text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-[#003366] text-white sticky top-0 z-50 no-print">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="CodeXero" className="h-8" />
            <span className="text-lg font-[700]">Admin Dashboard</span>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleLogout}
            data-testid="admin-logout-btn"
            className="text-white/80 hover:text-white hover:bg-white/10"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      {/* Stats Cards */}
      <div className="max-w-7xl mx-auto px-6 py-6 no-print">
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#008080]/10 rounded-sm">
                <Users className="h-5 w-5 text-[#008080]" />
              </div>
              <div>
                <p className="text-2xl font-[800] text-[#003366]">{stats.total_users || 0}</p>
                <p className="text-sm text-slate-500">Total Users</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#008080]/10 rounded-sm">
                <FileText className="h-5 w-5 text-[#008080]" />
              </div>
              <div>
                <p className="text-2xl font-[800] text-[#003366]">{stats.total_assessments || 0}</p>
                <p className="text-sm text-slate-500">Assessments</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-sm">
                <BarChart3 className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-[800] text-[#003366]">{stats.submitted_assessments || 0}</p>
                <p className="text-sm text-slate-500">Submitted</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('pii-leads')} data-testid="stat-card-leads">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-sm">
                <Megaphone className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-[800] text-[#003366]">{stats.total_leads || 0}</p>
                <p className="text-sm text-slate-500">PII Leads</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#008080]/10 rounded-sm">
                <CreditCard className="h-5 w-5 text-[#008080]" />
              </div>
              <div>
                <p className="text-2xl font-[800] text-[#003366]">{stats.total_payments || 0}</p>
                <p className="text-sm text-slate-500">Payments</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-sm">
                <CreditCard className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-[800] text-[#003366]">
                  ₹{((stats.total_revenue || 0) / 100).toLocaleString('en-IN')}
                </p>
                <p className="text-sm text-slate-500">Revenue</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 pb-12">
        <div className="bg-white border border-slate-200 rounded-sm">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div className="border-b border-slate-200 px-6 pt-4 no-print">
              <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-4">
                <TabsList className="bg-transparent p-0 h-auto flex-wrap">
                  <TabsTrigger value="evidence" data-testid="tab-evidence"
                    className="data-[state=active]:bg-[#008080]/10 data-[state=active]:text-[#008080] rounded-sm">
                    <FileText className="h-4 w-4 mr-2" />
                    Evidence
                  </TabsTrigger>
                  <TabsTrigger 
                    value="assessments" 
                    data-testid="tab-assessments"
                    className="data-[state=active]:bg-[#008080]/10 data-[state=active]:text-[#008080] rounded-sm"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Assessments
                  </TabsTrigger>
                  <TabsTrigger 
                    value="report-viewer"
                    data-testid="tab-report-viewer"
                    className="data-[state=active]:bg-[#008080]/10 data-[state=active]:text-[#008080] rounded-sm"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Report Viewer
                  </TabsTrigger>
                  <TabsTrigger 
                    value="users"
                    data-testid="tab-users"
                    className="data-[state=active]:bg-[#008080]/10 data-[state=active]:text-[#008080] rounded-sm"
                  >
                    <Users className="h-4 w-4 mr-2" />
                    Users
                  </TabsTrigger>
                  <TabsTrigger 
                    value="questions"
                    data-testid="tab-questions"
                    className="data-[state=active]:bg-[#008080]/10 data-[state=active]:text-[#008080] rounded-sm"
                  >
                    <HelpCircle className="h-4 w-4 mr-2" />
                    Question Bank
                  </TabsTrigger>
                  <TabsTrigger 
                    value="payments"
                    data-testid="tab-payments"
                    className="data-[state=active]:bg-[#008080]/10 data-[state=active]:text-[#008080] rounded-sm"
                  >
                    <CreditCard className="h-4 w-4 mr-2" />
                    Payments
                  </TabsTrigger>
                  <TabsTrigger 
                    value="admin-users"
                    data-testid="tab-admin-users"
                    className="data-[state=active]:bg-[#008080]/10 data-[state=active]:text-[#008080] rounded-sm"
                  >
                    <Shield className="h-4 w-4 mr-2" />
                    Admin Users
                  </TabsTrigger>
                  <TabsTrigger 
                    value="access-requests"
                    data-testid="tab-access-requests"
                    className="data-[state=active]:bg-[#008080]/10 data-[state=active]:text-[#008080] rounded-sm relative"
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Access Requests
                    {accessRequests.filter(r => r.status === 'pending').length > 0 && (
                      <span className="ml-2 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[20px] text-center">
                        {accessRequests.filter(r => r.status === 'pending').length}
                      </span>
                    )}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="pii-leads"
                    data-testid="tab-pii-leads"
                    className="data-[state=active]:bg-[#008080]/10 data-[state=active]:text-[#008080] rounded-sm"
                  >
                    <Megaphone className="h-4 w-4 mr-2" />
                    PII Leads
                    {piiLeads.length > 0 && (
                      <span className="ml-2 bg-purple-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[20px] text-center">
                        {piiLeads.length}
                      </span>
                    )}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="llm-usage"
                    data-testid="tab-llm-usage"
                    className="data-[state=active]:bg-[#008080]/10 data-[state=active]:text-[#008080] rounded-sm"
                  >
                    <Cpu className="h-4 w-4 mr-2" />
                    LLM Usage
                  </TabsTrigger>
                </TabsList>

                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 w-64 rounded-sm"
                      data-testid="search-input"
                    />
                  </div>
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={fetchAllData}
                    data-testid="refresh-btn"
                    className="rounded-sm"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Stage 0 Evidence Audit Tab */}
            <TabsContent value="evidence" className="p-0">
              <EvidenceTab assessmentId={selectedAssessment?.assessment?.id || selectedAssessment?.id} />
            </TabsContent>

            {/* Assessments Tab */}
            <TabsContent value="assessments" className="p-0">
              <div className="p-4 border-b border-slate-200 flex items-center gap-3">
                <Select value={sdfFilter} onValueChange={setSdfFilter}>
                  <SelectTrigger className="w-48 rounded-sm" data-testid="sdf-filter-dropdown">
                    <SelectValue placeholder="SDF Risk Filter" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All SDF Levels</SelectItem>
                    <SelectItem value="HIGH RISK">HIGH RISK</SelectItem>
                    <SelectItem value="PROBABLE">PROBABLE</SelectItem>
                    <SelectItem value="LIKELY">LIKELY</SelectItem>
                    <SelectItem value="POSSIBLE">POSSIBLE</SelectItem>
                    <SelectItem value="UNLIKELY">UNLIKELY</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-sm text-slate-500">{filteredAssessments.length} assessments</span>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Assessment ID</TableHead>
                      <TableHead>Organisation</TableHead>
                      <TableHead>Industry</TableHead>
                      <TableHead>Score</TableHead>
                      <TableHead>Readiness Band</TableHead>
                      <TableHead>SDF Risk</TableHead>
                      <TableHead>Evidence (P10)</TableHead>
                      <TableHead>Roadmap (P11)</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAssessments.map((assessment) => {
                      const p10 = getPillarScore(assessment, 'P10');
                      const p11 = getPillarScore(assessment, 'P11');
                      return (
                      <TableRow key={assessment.id} className="hover:bg-slate-50" data-testid={`assessment-row-${assessment.id}`}>
                        <TableCell className="font-mono text-sm">{assessment.assessment_ref_id}</TableCell>
                        <TableCell className="font-medium">{assessment.org_name}</TableCell>
                        <TableCell>{assessment.industry}</TableCell>
                        <TableCell>
                          {assessment.overall_score !== null && assessment.overall_score !== undefined ? (
                            <span className="font-[700] text-[#003366]">{assessment.overall_score}%</span>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          {assessment.readiness_band ? (
                            <Badge variant="outline">{assessment.readiness_band}</Badge>
                          ) : assessment.maturity_label ? (
                            <Badge variant="outline">L{assessment.maturity_level} - {assessment.maturity_label}</Badge>
                          ) : '-'}
                        </TableCell>
                        <TableCell data-testid={`sdf-risk-col-${assessment.id}`}>
                          {getSdfRiskBadge(assessment)}
                        </TableCell>
                        <TableCell data-testid={`evidence-score-col-${assessment.id}`}>
                          {p10 ? (
                            <span className="font-[600] text-sm">
                              <span className={p10.rag === 'GREEN' ? 'text-green-700' : p10.rag === 'RED' ? 'text-red-700' : 'text-amber-700'}>{p10.raw}</span>
                              <span className="text-slate-400">/{p10.max}</span>
                            </span>
                          ) : '-'}
                        </TableCell>
                        <TableCell data-testid={`roadmap-score-col-${assessment.id}`}>
                          {p11 ? (
                            <span className="font-[600] text-sm">
                              <span className={p11.rag === 'GREEN' ? 'text-green-700' : p11.rag === 'RED' ? 'text-red-700' : 'text-amber-700'}>{p11.raw}</span>
                              <span className="text-slate-400">/{p11.max}</span>
                            </span>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            assessment.status === 'submitted' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-amber-100 text-amber-800'
                          }>
                            {assessment.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {assessment.status === 'submitted' && (
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openReportViewer(assessment)}
                                data-testid={`view-report-${assessment.id}`}
                                className="rounded-sm"
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                View
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                      );
                    })}
                    {filteredAssessments.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center py-8 text-slate-500">
                          No assessments found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            {/* Report Viewer Tab */}
            <TabsContent value="report-viewer" className="p-0">
              {!selectedReportAssessment ? (
                <div className="text-center py-16 text-slate-500">
                  <ClipboardList className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-lg font-medium">No Report Selected</p>
                  <p className="text-sm mt-2">Go to Assessments tab and click "View Report" to view a report</p>
                </div>
              ) : !reportDetails ? (
                <div className="text-center py-16">
                  <Loader2 className="h-8 w-8 animate-spin text-[#008080] mx-auto" />
                </div>
              ) : (
                <div className="p-6">
                  {/* Report Actions Bar */}
                  <div className="flex items-center justify-between mb-6 pb-4 border-b no-print">
                    <div>
                      <h2 className="text-xl font-[800] text-[#003366]">
                        {reportDetails.assessment?.assessment_ref_id}
                      </h2>
                      <p className="text-slate-600">
                        {reportDetails.org_profile?.org_name} | {reportDetails.org_profile?.industry}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="default"
                        onClick={generateDocxReport}
                        disabled={generatingReport}
                        className="rounded-sm bg-[#003366] hover:bg-[#002244]"
                        data-testid="generate-report-btn"
                      >
                        {generatingReport ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <FileText className="h-4 w-4 mr-2" />
                        )}
                        {generatingReport ? 'Generating (AI-enriched)...' : 'Generate DOCX'}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={downloadDocxReport}
                        disabled={downloadingReport || generatingReport || !reportDetails?.report?.has_docx}
                        className="rounded-sm"
                        data-testid="download-report-btn"
                      >
                        {downloadingReport ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4 mr-2" />
                        )}
                        Download DOCX
                      </Button>
                      <Button
                        variant="outline"
                        onClick={openReportPreview}
                        disabled={loadingPreview || !reportDetails?.report?.has_docx}
                        className="rounded-sm border-[#008080] text-[#008080] hover:bg-[#008080]/10"
                        data-testid="preview-report-btn"
                        title={!reportDetails?.report?.has_docx ? 'Generate the DOCX first to preview' : 'Preview HTML report'}
                      >
                        {loadingPreview ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <BookOpen className="h-4 w-4 mr-2" />
                        )}
                        Preview Report
                      </Button>
                      <Button variant="outline" onClick={printReport} className="rounded-sm">
                        <Printer className="h-4 w-4 mr-2" />
                        Print / PDF
                      </Button>
                      <Button variant="outline" onClick={copyShareableLink} className="rounded-sm">
                        <Link2 className="h-4 w-4 mr-2" />
                        Copy Link
                      </Button>
                      <Button
                        variant={reportDetails.report?.report_shared ? 'default' : 'outline'}
                        onClick={() => updateReportStatus(selectedReportAssessment.id, 'report_shared', !reportDetails.report?.report_shared)}
                        className="rounded-sm"
                      >
                        {reportDetails.report?.report_shared ? 'Report Shared' : 'Mark as Shared'}
                      </Button>
                    </div>
                  </div>

                  {reportDetails.report?.llm_usage?.model && (
                    <div
                      className="flex items-center gap-4 mb-6 -mt-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-sm text-xs text-slate-600 no-print"
                      data-testid="report-llm-usage-badge"
                    >
                      <Cpu className="h-3.5 w-3.5 text-[#008080]" />
                      <span>Model: <span className="font-medium">{reportDetails.report.llm_usage.model}</span></span>
                      <span>Tokens: <span className="font-medium">{reportDetails.report.llm_usage.total_tokens?.toLocaleString()}</span></span>
                      <span>Cost: <span className="font-medium">${reportDetails.report.llm_usage.estimated_cost_usd?.toFixed(4)}</span></span>
                      <span>Calls: <span className="font-medium">{reportDetails.report.llm_usage.successful_calls}/{reportDetails.report.llm_usage.call_count}</span></span>
                    </div>
                  )}

                  {/* Report Content */}
                  <div className="space-y-8 report-content">
                    {/* Cover Section */}
                    <div className="bg-[#003366] text-white p-8 rounded-sm">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-sm text-slate-300 mb-2">DPDP Readiness Assessment Report</p>
                          <h1 className="text-3xl font-[800] mb-4">{reportDetails.org_profile?.org_name}</h1>
                          <div className="space-y-1 text-slate-300">
                            <p>{reportDetails.org_profile?.industry} | {reportDetails.org_profile?.org_size}</p>
                            <p>{reportDetails.org_profile?.city_state}</p>
                            <p className="mt-4">Assessed by: {reportDetails.org_profile?.contact_name}, {reportDetails.org_profile?.designation}</p>
                            <p>Assessment Date: {new Date(reportDetails.assessment?.submitted_at || Date.now()).toLocaleDateString()}</p>
                          </div>
                        </div>
                        <img src={LOGO_URL} alt="CodeXero" className="h-12" />
                      </div>
                      <div className="mt-6 pt-4 border-t border-white/20 text-sm text-slate-400">
                        Report ID: {reportDetails.assessment?.assessment_ref_id} | Aligned to DPDP Rules 2025 — v1.0
                      </div>
                    </div>

                    {/* Executive Summary */}
                    <div className="border border-slate-200 rounded-sm p-6">
                      <h2 className="text-xl font-[800] text-[#003366] mb-4 flex items-center gap-2">
                        <BarChart3 className="h-5 w-5" />
                        Executive Summary
                      </h2>
                      
                      <p className="text-slate-600 mb-4">
                        {reportDetails.org_profile?.org_name} completed the CX0 DPDP Readiness Assessment on{' '}
                        {new Date(reportDetails.assessment?.submitted_at || Date.now()).toLocaleDateString()}. 
                        Based on responses across the assessment's compliance pillars, the organisation has achieved an overall 
                        Weighted DPDP Readiness Score of <strong>{reportDetails.scoring?.overall_score}%</strong>, 
                        placing it in the <strong>{reportDetails.scoring?.readiness_band || reportDetails.scoring?.maturity_label}</strong> band.
                      </p>
                      <p className="text-slate-600 mb-4 italic">
                        {reportDetails.scoring?.maturity_description}
                      </p>
                      <p className="text-slate-600 mb-6">
                        {reportDetails.scoring?.total_critical} critical gaps and {reportDetails.scoring?.total_major} major gaps 
                        were identified, requiring structured remediation to achieve compliance ahead of the May 13, 2027 deadline.
                      </p>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-slate-50 p-4 rounded-sm text-center">
                          <p className="text-4xl font-[900] text-[#003366]">{reportDetails.scoring?.overall_score}%</p>
                          <p className="text-sm text-slate-500 mt-1">Weighted Readiness Score</p>
                        </div>
                        <div className="bg-slate-50 p-4 rounded-sm text-center">
                          <p className="text-lg font-[800] text-[#003366]">
                            {reportDetails.scoring?.readiness_band || `Level ${reportDetails.scoring?.maturity_level}`}
                          </p>
                          <p className="text-sm text-slate-500 mt-1">{reportDetails.scoring?.maturity_label}</p>
                        </div>
                        <div className="bg-slate-50 p-4 rounded-sm text-center">
                          {getRiskBadge(reportDetails.scoring?.readiness_rag || reportDetails.scoring?.penalty_risk)}
                          <p className="text-base font-[700] text-[#003366] mt-2">{reportDetails.scoring?.penalty_exposure_text || reportDetails.scoring?.penalty_exposure}</p>
                          <p className="text-sm text-slate-500">Penalty Exposure</p>
                        </div>
                        <div className={`p-4 rounded-sm text-center border ${reportDetails.scoring?.sdf_flag ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`} data-testid="sdf-badge-detail">
                          {reportDetails.scoring?.sdf_flag ? (
                            <>
                              <AlertTriangle className="h-8 w-8 text-red-600 mx-auto" />
                              <p className="text-sm font-[700] text-red-800 mt-1">HIGH RISK</p>
                              <p className="text-xs text-red-600">SDF Designated</p>
                            </>
                          ) : (() => {
                            let sdfText = 'N/A';
                            try {
                              const p9 = JSON.parse(reportDetails.scoring?.pillar_scores_json || '{}').pillars?.P9;
                              if (p9) {
                                sdfText = p9.rag === 'RED' ? 'PROBABLE' : p9.rag === 'ORANGE' ? 'LIKELY' : p9.rag === 'AMBER' ? 'POSSIBLE' : 'UNLIKELY';
                              }
                            } catch (err) {
                              console.warn('Failed to parse P9 SDF pillar score:', err);
                            }
                            const sdfColors = { PROBABLE: 'text-orange-700', LIKELY: 'text-amber-700', POSSIBLE: 'text-yellow-700', UNLIKELY: 'text-green-700' };
                            return (
                              <>
                                <p className={`text-2xl font-[800] ${sdfColors[sdfText] || 'text-slate-500'}`}>{sdfText}</p>
                                <p className="text-sm text-slate-500 mt-1">SDF Risk</p>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </div>

                    {/* Pillar-wise Compliance Heatmap */}
                    <div className="border border-slate-200 rounded-sm p-6">
                      <h2 className="text-xl font-[800] text-[#003366] mb-4">Pillar-wise Compliance Heatmap</h2>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Pillar</TableHead>
                            <TableHead>Questions</TableHead>
                            <TableHead>Raw Score</TableHead>
                            <TableHead>% Score</TableHead>
                            <TableHead>RAG Status</TableHead>
                            <TableHead>Band / Priority</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {reportDetails.scoring?.pillar_scores_json && 
                            Object.entries(JSON.parse(reportDetails.scoring.pillar_scores_json).pillars || {}).map(([code, data]) => {
                              const pillar = reportDetails.pillars?.find(p => p.code === code);
                              return (
                                <TableRow key={code}>
                                  <TableCell className="font-medium">{pillar?.name || code}</TableCell>
                                  <TableCell>{pillar?.question_count || '-'}</TableCell>
                                  <TableCell>{data.raw}/{data.max}</TableCell>
                                  <TableCell className="font-[700]">{data.pct}%</TableCell>
                                  <TableCell>{getRAGBadge(data.rag)}</TableCell>
                                  <TableCell>{data.band ? <Badge variant="outline">{data.band}</Badge> : data.priority}</TableCell>
                                </TableRow>
                              );
                            })
                          }
                        </TableBody>
                      </Table>
                    </div>

                    {/* Gap Register */}
                    <div className="border border-slate-200 rounded-sm p-6">
                      <h2 className="text-xl font-[800] text-[#003366] mb-4 flex items-center gap-2" data-testid="gap-register-heading">
                        <AlertTriangle className="h-5 w-5" />
                        Gap Register
                      </h2>
                      <div className="flex flex-wrap gap-3 mb-4">
                        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-sm px-3 py-1.5">
                          <span className="h-2.5 w-2.5 rounded-full bg-red-500"></span>
                          <span className="text-sm font-[600] text-red-800">{reportDetails.scoring?.total_critical} Critical</span>
                        </div>
                        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-sm px-3 py-1.5">
                          <span className="h-2.5 w-2.5 rounded-full bg-amber-500"></span>
                          <span className="text-sm font-[600] text-amber-800">{reportDetails.scoring?.total_major} Major</span>
                        </div>
                        <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-sm px-3 py-1.5">
                          <span className="h-2.5 w-2.5 rounded-full bg-blue-500"></span>
                          <span className="text-sm font-[600] text-blue-800">{reportDetails.scoring?.total_minor} Minor</span>
                        </div>
                        <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-sm px-3 py-1.5">
                          <span className="h-2.5 w-2.5 rounded-full bg-green-500"></span>
                          <span className="text-sm font-[600] text-green-800">{reportDetails.scoring?.total_pass} Pass</span>
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Q#</TableHead>
                              <TableHead>Pillar</TableHead>
                              <TableHead>Gap Title</TableHead>
                              <TableHead>Answer</TableHead>
                              <TableHead>Severity</TableHead>
                              <TableHead>Recommendation</TableHead>
                              <TableHead>Evidence Required</TableHead>
                              <TableHead>Owner</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {reportDetails.answers?.filter(a => a.gap_tag !== 'PASS' && !a.is_auto_scored)
                              .sort((a, b) => {
                                const order = { CRITICAL: 0, MAJOR: 1, MINOR: 2 };
                                return (order[a.gap_tag] ?? 3) - (order[b.gap_tag] ?? 3);
                              })
                              .map(answer => {
                              const question = reportDetails.questions?.find(q => q.id === answer.question_id);
                              return (
                                <TableRow key={answer.id} data-testid={`gap-row-${question?.question_number}`}>
                                  <TableCell className="font-mono">{question?.question_number}</TableCell>
                                  <TableCell><Badge variant="outline">{question?.pillar_code}</Badge></TableCell>
                                  <TableCell className="font-medium max-w-[160px]">{question?.gap_title}</TableCell>
                                  <TableCell className="font-medium">{answer.answer_option}</TableCell>
                                  <TableCell>{getGapBadge(answer.gap_tag)}</TableCell>
                                  <TableCell className="text-sm max-w-[200px]">{question?.gap_recommendation}</TableCell>
                                  <TableCell className="text-sm text-slate-500 max-w-[140px]">{question?.evidence_artefact}</TableCell>
                                  <TableCell className="text-sm">{question?.owner_function}</TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    </div>

                    {/* Recommendation Roadmap */}
                    <div className="border border-slate-200 rounded-sm p-6" data-testid="recommendation-roadmap">
                      <h2 className="text-xl font-[800] text-[#003366] mb-4 flex items-center gap-2">
                        <CheckCircle className="h-5 w-5" />
                        Recommendation Roadmap
                      </h2>
                      <p className="text-slate-600 mb-6">
                        Phased remediation plan based on gap severity, mapped to DPDP Act compliance deadlines.
                      </p>
                      {[
                        { phase: 1, title: "Phase 1: Foundation", timeline: "0 — 90 days", tag: "CRITICAL", color: "border-red-400 bg-red-50", headerBg: "bg-red-100 text-red-900" },
                        { phase: 2, title: "Phase 2: Implementation", timeline: "90 — 180 days", tag: "MAJOR", color: "border-amber-400 bg-amber-50", headerBg: "bg-amber-100 text-amber-900" },
                        { phase: 3, title: "Phase 3: Optimization", timeline: "180 — 365 days", tag: "MINOR", color: "border-blue-400 bg-blue-50", headerBg: "bg-blue-100 text-blue-900" },
                        { phase: 4, title: "Phase 4: Continuous Compliance", timeline: "12+ months", tag: "PASS", color: "border-green-400 bg-green-50", headerBg: "bg-green-100 text-green-900" }
                      ].map(phaseInfo => {
                        const phaseGaps = reportDetails.answers?.filter(a => {
                          if (a.is_auto_scored) return false;
                          return a.gap_tag === phaseInfo.tag;
                        }) || [];
                        
                        if (phaseGaps.length === 0 && phaseInfo.tag !== "PASS") return null;
                        
                        return (
                          <div key={phaseInfo.phase} className={`border-l-4 ${phaseInfo.color} rounded-sm mb-4 overflow-hidden`}>
                            <div className={`px-4 py-2 ${phaseInfo.headerBg} flex items-center justify-between`}>
                              <span className="font-[700] text-sm">{phaseInfo.title} ({phaseInfo.timeline})</span>
                              <Badge variant="outline" className="font-mono">{phaseGaps.length} items</Badge>
                            </div>
                            {phaseGaps.length > 0 ? (
                              <div className="divide-y divide-slate-100">
                                {phaseGaps.map(answer => {
                                  const question = reportDetails.questions?.find(q => q.id === answer.question_id);
                                  return (
                                    <div key={answer.id} className="px-4 py-3 flex items-start gap-4">
                                      <span className="font-mono text-sm text-slate-500 whitespace-nowrap pt-0.5">{question?.question_number}</span>
                                      <div className="flex-1 min-w-0">
                                        <p className="font-[600] text-sm text-slate-800">{question?.gap_title}</p>
                                        <p className="text-sm text-slate-600 mt-0.5">{question?.gap_recommendation}</p>
                                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-slate-400">
                                          <span>Evidence: {question?.evidence_artefact}</span>
                                          <span>Owner: {question?.owner_function}</span>
                                          <span>Effort: {question?.effort}</span>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="px-4 py-3 text-sm text-slate-500">No items in this phase</div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Industry Observations */}
                    {reportDetails.industry_observations?.length > 0 && (
                      <div className="border border-slate-200 rounded-sm p-6" data-testid="industry-observations">
                        <h2 className="text-xl font-[800] text-[#003366] mb-4">Industry-Specific Observations</h2>
                        <p className="text-slate-600 mb-4">
                          Key compliance considerations for the <strong>{reportDetails.org_profile?.industry}</strong> sector:
                        </p>
                        <div className="space-y-2">
                          {reportDetails.industry_observations.map((obs, idx) => (
                            <div key={obs.id || idx} className="flex items-start gap-3 bg-slate-50 rounded-sm p-3">
                              <span className="text-[#008080] font-[700] text-sm mt-0.5">{idx + 1}.</span>
                              <p className="text-sm text-slate-700">{obs.observation_text}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Complete Answer Record */}
                    <div className="border border-slate-200 rounded-sm p-6">
                      <h2 className="text-xl font-[800] text-[#003366] mb-4">Complete Answer Record</h2>
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Q#</TableHead>
                              <TableHead>Pillar</TableHead>
                              <TableHead>Question</TableHead>
                              <TableHead>Answer</TableHead>
                              <TableHead>Score</TableHead>
                              <TableHead>Gap Tag</TableHead>
                              <TableHead>Phase</TableHead>
                              <TableHead>Auto</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {reportDetails.questions?.map(question => {
                              const answer = reportDetails.answers?.find(a => a.question_id === question.id);
                              const qScores = reportDetails.scoring?.pillar_scores_json 
                                ? JSON.parse(reportDetails.scoring.pillar_scores_json).question_scores?.[question.question_number] 
                                : null;
                              return (
                                <TableRow key={question.id}>
                                  <TableCell className="font-mono">{question.question_number}</TableCell>
                                  <TableCell><Badge variant="outline">{question.pillar_code}</Badge></TableCell>
                                  <TableCell className="max-w-md truncate">{question.question_text}</TableCell>
                                  <TableCell className="font-medium">{answer?.answer_option || '-'}</TableCell>
                                  <TableCell>{answer?.score || '-'}</TableCell>
                                  <TableCell>{answer?.gap_tag ? getGapBadge(answer.gap_tag) : '-'}</TableCell>
                                  <TableCell>
                                    {qScores?.recommendation_phase ? (
                                      <Badge variant="outline" className="text-xs">P{qScores.recommendation_phase}</Badge>
                                    ) : '-'}
                                  </TableCell>
                                  <TableCell>{answer?.is_auto_scored ? 'Yes' : 'No'}</TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    </div>

                    {/* Admin Notes */}
                    <div className="border border-slate-200 rounded-sm p-6 no-print">
                      <h2 className="text-xl font-[800] text-[#003366] mb-4">Admin Notes</h2>
                      <Textarea
                        value={adminNotes}
                        onChange={(e) => setAdminNotes(e.target.value)}
                        placeholder="Add internal notes about this assessment..."
                        className="min-h-[100px] mb-4"
                      />
                      <Button onClick={saveAdminNotes} className="bg-[#003366] hover:bg-[#002244] rounded-sm">
                        <Save className="h-4 w-4 mr-2" />
                        Save Notes
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Users Tab */}
            <TabsContent value="users" className="p-0">
              <div className="p-4 border-b border-slate-200 flex justify-between items-center">
                <div>
                  <h3 className="font-[700] text-[#003366]">User Management</h3>
                  <p className="text-sm text-slate-500">{users.length} registered users</p>
                </div>
                <Button 
                  onClick={openCreateUser}
                  className="bg-[#003366] hover:bg-[#002244] rounded-sm"
                  data-testid="create-user-btn"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Create New User
                </Button>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Username</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Organisation</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Payment Date</TableHead>
                      <TableHead>Assessment</TableHead>
                      <TableHead>Stage 0</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.map((user) => (
                      <TableRow key={user.id} className="hover:bg-slate-50">
                        <TableCell className="font-mono text-sm">
                          {user.display_username}
                          {user.review_needed && (
                            <Badge className="ml-2 bg-amber-100 text-amber-800">Review</Badge>
                          )}
                        </TableCell>
                        <TableCell>{user.first_name} {user.last_name}</TableCell>
                        <TableCell>{user.org_name}</TableCell>
                        <TableCell>{user.work_email}</TableCell>
                        <TableCell>
                          {user.payment_date ? new Date(user.payment_date).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            user.assessment_status === 'submitted' 
                              ? 'bg-green-100 text-green-800' 
                              : user.assessment_status === 'in_progress'
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-slate-100 text-slate-600'
                          }>
                            {user.assessment_status || 'not_started'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Stage0Toggle userId={user.id} enabled={user.stage0_enabled} />
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            user.account_status === 'active' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }>
                            {user.account_status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => resendCredentials(user.id)}
                              title="Resend Credentials"
                              className="h-8 w-8 p-0"
                            >
                              <Mail className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => resetAssessment(user.id)}
                              title="Reset Assessment"
                              className="h-8 w-8 p-0"
                            >
                              <RotateCcw className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => revokeAccess(user.id)}
                              title="Revoke Access"
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                            >
                              <UserX className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredUsers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                          No users found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            {/* Questions Tab - Question Bank Management */}
            <TabsContent value="questions" className="p-0">
              <div className="p-4 border-b border-slate-200 flex justify-between items-center">
                <div>
                  <h3 className="font-[700] text-[#003366]">Question Bank Management</h3>
                  <p className="text-sm text-slate-500">{questions.length} questions across {pillars.length} pillars</p>
                </div>
                <Button 
                  onClick={() => openQuestionEditor()}
                  className="bg-[#003366] hover:bg-[#002244] rounded-sm"
                  data-testid="add-question-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add New Question
                </Button>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="w-16">Q#</TableHead>
                      <TableHead className="w-20">Pillar</TableHead>
                      <TableHead>Question Text</TableHead>
                      <TableHead className="w-20">Phase</TableHead>
                      <TableHead className="w-20">Effort</TableHead>
                      <TableHead className="w-20">Active</TableHead>
                      <TableHead className="w-24">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredQuestions.map((question) => (
                      <TableRow key={question.id} className="hover:bg-slate-50">
                        <TableCell className="font-mono font-medium">{question.question_number}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{question.pillar_code}</Badge>
                        </TableCell>
                        <TableCell className="max-w-md">
                          <p className="line-clamp-2">{question.question_text}</p>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">Phase {question.phase}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            question.effort === 'High' ? 'bg-red-100 text-red-800' :
                            question.effort === 'Medium' ? 'bg-amber-100 text-amber-800' :
                            'bg-green-100 text-green-800'
                          }>
                            {question.effort}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Switch 
                            checked={question.is_active}
                            onCheckedChange={() => toggleQuestionActive(question)}
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openQuestionEditor(question)}
                            className="h-8 w-8 p-0"
                            data-testid={`edit-question-${question.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            {/* Payments Tab */}
            <TabsContent value="payments" className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Order ID</TableHead>
                      <TableHead>Organisation</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Payment ID</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPayments.map((payment) => (
                      <TableRow key={payment.id} className="hover:bg-slate-50">
                        <TableCell className="font-mono text-sm">{payment.razorpay_order_id}</TableCell>
                        <TableCell>{payment.org_name}</TableCell>
                        <TableCell>{payment.first_name} {payment.last_name}</TableCell>
                        <TableCell>{payment.work_email}</TableCell>
                        <TableCell className="font-[700] text-[#003366]">
                          ₹{(payment.amount / 100).toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell className="font-mono text-sm">{payment.razorpay_payment_id}</TableCell>
                        <TableCell>{new Date(payment.created_at).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Badge className={
                            payment.status === 'success' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }>
                            {payment.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredPayments.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                          No payments found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

      {/* Question Edit Modal */}
      <Dialog open={showQuestionModal} onOpenChange={setShowQuestionModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-sm">
          <DialogHeader>
            <DialogTitle className="text-[#003366]">
              {editingQuestion ? `Edit Question ${editingQuestion.question_number}` : 'Add New Question'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Question Number</Label>
                <Input
                  value={questionForm.question_number || ''}
                  onChange={(e) => handleQuestionFormChange('question_number', e.target.value)}
                  placeholder="Q42"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Pillar</Label>
                <Select 
                  value={questionForm.pillar_code || ''} 
                  onValueChange={(v) => handleQuestionFormChange('pillar_code', v)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select pillar" />
                  </SelectTrigger>
                  <SelectContent>
                    {pillars.map(p => (
                      <SelectItem key={p.code} value={p.code}>{p.code} - {p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Question Text</Label>
              <Textarea
                value={questionForm.question_text || ''}
                onChange={(e) => handleQuestionFormChange('question_text', e.target.value)}
                placeholder="Enter the question..."
                className="mt-1 min-h-[80px]"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Option A (Best - Score 4)</Label>
                <Textarea
                  value={questionForm.option_a || ''}
                  onChange={(e) => handleQuestionFormChange('option_a', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Option B (Score 3)</Label>
                <Textarea
                  value={questionForm.option_b || ''}
                  onChange={(e) => handleQuestionFormChange('option_b', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Option C (Score 2)</Label>
                <Textarea
                  value={questionForm.option_c || ''}
                  onChange={(e) => handleQuestionFormChange('option_c', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Option D (Worst - Score 1)</Label>
                <Textarea
                  value={questionForm.option_d || ''}
                  onChange={(e) => handleQuestionFormChange('option_d', e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Phase</Label>
                <Select 
                  value={String(questionForm.phase || 1)} 
                  onValueChange={(v) => handleQuestionFormChange('phase', parseInt(v))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Phase 1 (0-90 days)</SelectItem>
                    <SelectItem value="2">Phase 2 (90-180 days)</SelectItem>
                    <SelectItem value="3">Phase 3 (180-365 days)</SelectItem>
                    <SelectItem value="4">Phase 4 (12+ months)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Effort</Label>
                <Select 
                  value={questionForm.effort || 'Medium'} 
                  onValueChange={(v) => handleQuestionFormChange('effort', v)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Low">Low</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Gap Title</Label>
              <Input
                value={questionForm.gap_title || ''}
                onChange={(e) => handleQuestionFormChange('gap_title', e.target.value)}
                placeholder="Data Inventory Gap"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Gap Recommendation</Label>
              <Textarea
                value={questionForm.gap_recommendation || ''}
                onChange={(e) => handleQuestionFormChange('gap_recommendation', e.target.value)}
                placeholder="Recommended action to close the gap..."
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Evidence Artefact</Label>
                <Input
                  value={questionForm.evidence_artefact || ''}
                  onChange={(e) => handleQuestionFormChange('evidence_artefact', e.target.value)}
                  placeholder="Data inventory register"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Owner Function</Label>
                <Input
                  value={questionForm.owner_function || ''}
                  onChange={(e) => handleQuestionFormChange('owner_function', e.target.value)}
                  placeholder="DPO/IT"
                  className="mt-1"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Switch 
                checked={questionForm.is_active !== false}
                onCheckedChange={(v) => handleQuestionFormChange('is_active', v)}
              />
              <Label>Active</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQuestionModal(false)} className="rounded-sm">
              Cancel
            </Button>
            <Button 
              onClick={saveQuestion} 
              disabled={actionLoading}
              className="bg-[#003366] hover:bg-[#002244] rounded-sm"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              {editingQuestion ? 'Update Question' : 'Add Question'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create User Modal */}

      {/* Access Requests Tab */}
            <TabsContent value="access-requests" className="p-0">
              <div className="p-4 border-b border-slate-200">
                <h3 className="font-[700] text-[#003366]">Access Requests</h3>
                <p className="text-sm text-slate-500">
                  {accessRequests.filter(r => r.status === 'pending').length} pending / {accessRequests.length} total
                </p>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Organisation</TableHead>
                      <TableHead>Industry</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Submitted</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {accessRequests.map((req) => (
                      <TableRow key={req.id} className="hover:bg-slate-50" data-testid={`access-request-row-${req.id}`}>
                        <TableCell className="font-medium">{req.first_name} {req.last_name}</TableCell>
                        <TableCell className="text-sm">{req.work_email}</TableCell>
                        <TableCell className="text-sm">{req.org_name}</TableCell>
                        <TableCell className="text-sm">{req.industry || '—'}</TableCell>
                        <TableCell className="text-sm">{req.phone || '—'}</TableCell>
                        <TableCell>
                          <Badge className={
                            req.status === 'approved' ? 'bg-green-100 text-green-800' :
                            req.status === 'rejected' ? 'bg-red-100 text-red-800' :
                            'bg-amber-100 text-amber-800'
                          }>
                            {req.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {req.submitted_at ? new Date(req.submitted_at).toLocaleDateString() : '—'}
                        </TableCell>
                        <TableCell>
                          {req.status === 'pending' && (
                            <div className="flex gap-1">
                              <Button
                                size="sm" variant="outline"
                                onClick={() => approveAccessRequest(req.id)}
                                className="rounded-sm text-green-700 border-green-300 hover:bg-green-50 h-8"
                                data-testid={`approve-request-${req.id}`}
                              >
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm" variant="outline"
                                onClick={() => rejectAccessRequest(req.id)}
                                className="rounded-sm text-red-700 border-red-300 hover:bg-red-50 h-8"
                                data-testid={`reject-request-${req.id}`}
                              >
                                <X className="h-3 w-3 mr-1" />
                                Reject
                              </Button>
                            </div>
                          )}
                          {req.status !== 'pending' && (
                            <span className="text-xs text-slate-400">
                              by {req.reviewed_by || '—'}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {accessRequests.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-slate-500">No access requests yet</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

      {/* Admin Users Tab */}
            <TabsContent value="admin-users" className="p-0">
              <div className="p-4 border-b border-slate-200 flex justify-between items-center">
                <div>
                  <h3 className="font-[700] text-[#003366]">Admin User Management</h3>
                  <p className="text-sm text-slate-500">{adminUsers.length} admin accounts — domain: cx0.in</p>
                </div>
                <Button 
                  onClick={() => { setNewAdminUsername(''); setNewAdminDisplayName(''); setShowCreateAdmin(true); }}
                  className="bg-[#003366] hover:bg-[#002244] rounded-sm"
                  data-testid="create-admin-btn"
                >
                  <Shield className="h-4 w-4 mr-2" />
                  Create Admin User
                </Button>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Username</TableHead>
                      <TableHead>Display Name</TableHead>
                      <TableHead>Created By</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {adminUsers.map((au) => (
                      <TableRow key={au.id} className="hover:bg-slate-50" data-testid={`admin-row-${au.id}`}>
                        <TableCell className="font-mono text-sm font-medium">{au.username}</TableCell>
                        <TableCell>{au.display_name || '—'}</TableCell>
                        <TableCell className="text-sm text-slate-500">{au.created_by || 'System'}</TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {au.created_at ? new Date(au.created_at).toLocaleDateString() : '—'}
                        </TableCell>
                        <TableCell>
                          <Badge className={au.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                            {au.is_active !== false ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => resetAdminPassword(au.id)}
                              title="Reset Password"
                              className="h-8 w-8 p-0"
                              data-testid={`reset-pwd-${au.id}`}
                            >
                              <Key className="h-4 w-4" />
                            </Button>
                            {au.username !== 'sunnys@codexero.ai' && (
                              <>
                                <Button
                                  size="sm" variant="ghost"
                                  onClick={() => toggleAdminStatus(au.id)}
                                  title={au.is_active !== false ? 'Deactivate' : 'Activate'}
                                  className="h-8 w-8 p-0"
                                  data-testid={`toggle-admin-${au.id}`}
                                >
                                  <UserX className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm" variant="ghost"
                                  onClick={() => deleteAdmin(au.id, au.username)}
                                  title="Delete Admin"
                                  className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                                  data-testid={`delete-admin-${au.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {adminUsers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-slate-500">No admin users found</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            {/* PII Leads Tab */}
            <TabsContent value="pii-leads" className="p-0">
              <div className="p-4 border-b border-slate-200 flex flex-wrap items-center gap-3">
                <Select value={leadsIndustryFilter} onValueChange={setLeadsIndustryFilter}>
                  <SelectTrigger className="w-48 rounded-sm" data-testid="leads-industry-filter">
                    <SelectValue placeholder="Filter by Industry" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Industries</SelectItem>
                    {leadsIndustries.map(ind => (
                      <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={leadsGapFilter} onValueChange={setLeadsGapFilter}>
                  <SelectTrigger className="w-48 rounded-sm" data-testid="leads-gap-filter">
                    <SelectValue placeholder="Filter by Gap Level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Gap Levels</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="Low">Low</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-sm text-slate-500">{filteredLeads.length} leads</span>
                <div className="ml-auto">
                  <Button variant="outline" onClick={exportLeadsCSV} className="rounded-sm" data-testid="export-leads-csv-btn">
                    <Download className="h-4 w-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>Industry</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Size</TableHead>
                      <TableHead>Impact</TableHead>
                      <TableHead>Gap</TableHead>
                      <TableHead>SDF</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredLeads.map((lead, i) => (
                      <TableRow key={lead.id || i} className="hover:bg-slate-50" data-testid={`lead-row-${i}`}>
                        <TableCell className="font-medium whitespace-nowrap">{lead.name}</TableCell>
                        <TableCell className="text-sm">{lead.email}</TableCell>
                        <TableCell className="text-sm">{lead.company_name || '-'}</TableCell>
                        <TableCell>
                          {lead.industry ? (
                            <Badge variant="outline" className="text-xs">{lead.industry}</Badge>
                          ) : '-'}
                        </TableCell>
                        <TableCell className="text-sm capitalize">{lead.role?.replace(/_/g, ' ') || '-'}</TableCell>
                        <TableCell className="text-sm">{lead.company_size || '-'}</TableCell>
                        <TableCell>
                          {lead.impact_score != null ? (
                            <span className={`font-[700] text-sm ${
                              lead.impact_score >= 70 ? 'text-red-600' : lead.impact_score >= 40 ? 'text-amber-600' : 'text-green-600'
                            }`}>{lead.impact_score}</span>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          {lead.visibility_gap_level ? (
                            <Badge className={
                              lead.visibility_gap_level === 'High' ? 'bg-red-100 text-red-800' :
                              lead.visibility_gap_level === 'Medium' ? 'bg-amber-100 text-amber-800' :
                              'bg-green-100 text-green-800'
                            }>{lead.visibility_gap_level}</Badge>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          {lead.is_sdf ? (
                            <Badge className="bg-red-100 text-red-800 border border-red-300">SDF</Badge>
                          ) : (
                            <span className="text-slate-400 text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-slate-500 whitespace-nowrap">
                          {lead.submitted_at ? new Date(lead.submitted_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredLeads.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center py-8 text-slate-500">
                          No PII Finder leads captured yet
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            {/* LLM Usage Tab */}
            <TabsContent value="llm-usage" className="p-0">
              <div className="p-6">
                <div className="mb-4">
                  <h2 className="text-lg font-[800] text-[#003366]">LLM Report-Generation Spend</h2>
                  <p className="text-sm text-slate-500 mt-1">
                    Real token usage and estimated cost per model, captured from actual report generations. Compare current model spend against any prior baseline model.
                  </p>
                </div>
                <div className="overflow-x-auto border border-slate-200 rounded-sm">
                  <Table data-testid="llm-usage-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Model</TableHead>
                        <TableHead>Reports Generated</TableHead>
                        <TableHead>Input Tokens</TableHead>
                        <TableHead>Output Tokens</TableHead>
                        <TableHead>Total Cost (USD)</TableHead>
                        <TableHead>Avg Cost / Report</TableHead>
                        <TableHead>Last Generated</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {llmUsageSummary.map((m) => (
                        <TableRow key={m.model} data-testid={`llm-usage-row-${m.model}`}>
                          <TableCell className="font-medium">{m.model}</TableCell>
                          <TableCell>{m.report_count}</TableCell>
                          <TableCell>{m.total_input_tokens.toLocaleString()}</TableCell>
                          <TableCell>{m.total_output_tokens.toLocaleString()}</TableCell>
                          <TableCell>${m.total_cost_usd.toFixed(4)}</TableCell>
                          <TableCell>${m.avg_cost_per_report_usd.toFixed(4)}</TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {m.last_generated_at ? new Date(m.last_generated_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                      {llmUsageSummary.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                            No report generations tracked yet — generate a report to see usage here
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </TabsContent>

          </Tabs>
        </div>
      </div>

      {/* Create Admin User Dialog */}
      <Dialog open={showCreateAdmin} onOpenChange={setShowCreateAdmin}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader>
            <DialogTitle className="text-[#003366]">Create Admin User</DialogTitle>
            <DialogDescription>New admin user will be created with @cx0.in domain.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Username</Label>
              <div className="flex items-center gap-0 mt-1">
                <Input
                  placeholder="username"
                  value={newAdminUsername}
                  onChange={(e) => setNewAdminUsername(e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, ''))}
                  className="rounded-r-none rounded-sm"
                  data-testid="admin-username-input"
                />
                <span className="bg-slate-100 border border-l-0 border-slate-200 px-3 py-2 text-sm text-slate-600 rounded-r-sm whitespace-nowrap">@cx0.in</span>
              </div>
              {newAdminUsername && (
                <p className="text-xs text-slate-500 mt-1">Login ID: <span className="font-mono font-medium">{newAdminUsername}@cx0.in</span></p>
              )}
            </div>
            <div>
              <Label>Display Name <span className="text-slate-400">(optional)</span></Label>
              <Input
                placeholder="e.g. John Doe"
                value={newAdminDisplayName}
                onChange={(e) => setNewAdminDisplayName(e.target.value)}
                className="rounded-sm mt-1"
                data-testid="admin-display-name-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateAdmin(false)} className="rounded-sm">Cancel</Button>
            <Button 
              onClick={handleCreateAdmin}
              disabled={!newAdminUsername.trim() || actionLoading}
              className="bg-[#003366] hover:bg-[#002244] rounded-sm"
              data-testid="submit-create-admin-btn"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Shield className="h-4 w-4 mr-2" />}
              Create Admin
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Credentials Dialog */}
      <Dialog open={!!createdAdminCreds} onOpenChange={() => setCreatedAdminCreds(null)}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader>
            <DialogTitle className="text-[#003366]">Admin Credentials</DialogTitle>
            <DialogDescription>Share these credentials securely with the new admin.</DialogDescription>
          </DialogHeader>
          {createdAdminCreds && (
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 p-4 rounded-sm border border-slate-200 font-mono text-sm space-y-2">
                <p><span className="text-slate-500">Login URL:</span> <span className="font-medium">{window.location.origin}/admin</span></p>
                <p><span className="text-slate-500">Username:</span> <span className="font-medium">{createdAdminCreds.username}</span></p>
                <p><span className="text-slate-500">Password:</span> <span className="font-medium text-[#008080]">{createdAdminCreds.temp_password}</span></p>
              </div>
              <p className="text-xs text-amber-600">Please share this password securely. The admin should change it on first login.</p>
              <Button onClick={copyAdminCreds} variant="outline" className="w-full rounded-sm" data-testid="copy-admin-creds-btn">
                <Copy className="h-4 w-4 mr-2" />
                Copy Credentials
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create User Modal */}
      <Dialog open={showCreateUserModal} onOpenChange={setShowCreateUserModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-sm">
          <DialogHeader>
            <DialogTitle className="text-[#003366]">Create New User</DialogTitle>
            <DialogDescription>
              Create a new assessment user. Auto-generated login credentials will be displayed after creation.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>First Name <span className="text-red-500">*</span></Label>
                <Input
                  value={createUserForm.first_name}
                  onChange={(e) => handleCreateUserFormChange('first_name', e.target.value)}
                  placeholder="Priya"
                  className="mt-1"
                  data-testid="create-user-first-name"
                />
              </div>
              <div>
                <Label>Last Name <span className="text-red-500">*</span></Label>
                <Input
                  value={createUserForm.last_name}
                  onChange={(e) => handleCreateUserFormChange('last_name', e.target.value)}
                  placeholder="Sharma"
                  className="mt-1"
                  data-testid="create-user-last-name"
                />
              </div>
            </div>

            <div>
              <Label>Work Email <span className="text-red-500">*</span></Label>
              <Input
                type="email"
                value={createUserForm.work_email}
                onChange={(e) => handleCreateUserFormChange('work_email', e.target.value)}
                placeholder="priya.sharma@company.com"
                className="mt-1"
                data-testid="create-user-email"
              />
            </div>

            <div>
              <Label>Organisation Name <span className="text-red-500">*</span></Label>
              <Input
                value={createUserForm.org_name}
                onChange={(e) => handleCreateUserFormChange('org_name', e.target.value)}
                placeholder="Acme Corp"
                className="mt-1"
                data-testid="create-user-org"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Phone</Label>
                <Input
                  value={createUserForm.phone}
                  onChange={(e) => handleCreateUserFormChange('phone', e.target.value)}
                  placeholder="+91 98765 43210"
                  className="mt-1"
                  data-testid="create-user-phone"
                />
              </div>
              <div>
                <Label>Designation</Label>
                <Input
                  value={createUserForm.designation}
                  onChange={(e) => handleCreateUserFormChange('designation', e.target.value)}
                  placeholder="CTO"
                  className="mt-1"
                  data-testid="create-user-designation"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Industry</Label>
                <Select 
                  value={createUserForm.industry} 
                  onValueChange={(v) => handleCreateUserFormChange('industry', v)}
                >
                  <SelectTrigger className="mt-1" data-testid="create-user-industry">
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pharma Retail">Pharma Retail</SelectItem>
                    <SelectItem value="BFSI">BFSI</SelectItem>
                    <SelectItem value="Healthcare">Healthcare</SelectItem>
                    <SelectItem value="Retail">Retail</SelectItem>
                    <SelectItem value="IT/SaaS">IT/SaaS</SelectItem>
                    <SelectItem value="Manufacturing">Manufacturing</SelectItem>
                    <SelectItem value="Education">Education</SelectItem>
                    <SelectItem value="Others">Others</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Organisation Size</Label>
                <Select 
                  value={createUserForm.org_size} 
                  onValueChange={(v) => handleCreateUserFormChange('org_size', v)}
                >
                  <SelectTrigger className="mt-1" data-testid="create-user-org-size">
                    <SelectValue placeholder="Select size" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1-10">1-10</SelectItem>
                    <SelectItem value="11-50">11-50</SelectItem>
                    <SelectItem value="51-200">51-200</SelectItem>
                    <SelectItem value="201-500">201-500</SelectItem>
                    <SelectItem value="501-1000">501-1000</SelectItem>
                    <SelectItem value="1000+">1000+</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-sm p-4">
              <Switch 
                checked={createUserForm.payment_received}
                onCheckedChange={(v) => handleCreateUserFormChange('payment_received', v)}
                data-testid="create-user-payment-toggle"
              />
              <div>
                <Label className="font-[600]">Payment Received</Label>
                <p className="text-xs text-slate-500 mt-0.5">Mark if offline bank transfer has been received</p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateUserModal(false)} className="rounded-sm">
              Cancel
            </Button>
            <Button 
              onClick={submitCreateUser} 
              disabled={actionLoading}
              className="bg-[#003366] hover:bg-[#002244] rounded-sm"
              data-testid="create-user-submit-btn"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credentials Display Modal */}
      <Dialog open={showCredentialsModal} onOpenChange={setShowCredentialsModal}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader>
            <DialogTitle className="text-[#003366] flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              User Created Successfully
            </DialogTitle>
            <DialogDescription>
              Share these credentials with the user. They will be prompted to change their password on first login.
            </DialogDescription>
          </DialogHeader>
          
          {createdCredentials && (
            <div className="py-4 space-y-4">
              <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 space-y-3">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Username</p>
                  <p className="text-lg font-mono font-[700] text-[#003366]" data-testid="created-username">
                    {createdCredentials.display_username}
                  </p>
                </div>
                <div className="border-t border-slate-200 pt-3">
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Temporary Password</p>
                  <p className="text-lg font-mono font-[700] text-[#003366]" data-testid="created-password">
                    {createdCredentials.temp_password}
                  </p>
                </div>
                <div className="border-t border-slate-200 pt-3">
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Email</p>
                  <p className="text-sm text-slate-700">{createdCredentials.work_email}</p>
                </div>
              </div>

              {createdCredentials.email_sent === false && (
                <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-sm text-amber-800">
                  Email delivery not configured. Please share the credentials manually.
                </div>
              )}

              <Button 
                onClick={copyCredentials} 
                variant="outline" 
                className="w-full rounded-sm"
                data-testid="copy-credentials-btn"
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy Credentials to Clipboard
              </Button>
            </div>
          )}

          <DialogFooter>
            <Button 
              onClick={() => setShowCredentialsModal(false)} 
              className="bg-[#003366] hover:bg-[#002244] rounded-sm w-full"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* HTML Report Preview Modal */}
      <Dialog open={showPreviewModal} onOpenChange={(open) => { setShowPreviewModal(open); if (!open) setPreviewHtml(''); }}>
        <DialogContent
          className="max-w-5xl w-full rounded-sm"
          style={{ maxHeight: '92vh', display: 'flex', flexDirection: 'column', padding: 0 }}
          data-testid="report-preview-modal"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0">
            <div className="flex items-center gap-3">
              <BookOpen className="h-5 w-5 text-[#008080]" />
              <div>
                <DialogTitle className="text-[#003366] text-base font-[800]">
                  Report Preview
                </DialogTitle>
                <p className="text-xs text-slate-500 mt-0.5">
                  {reportDetails?.org_profile?.org_name} — {reportDetails?.assessment?.assessment_ref_id}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                className="rounded-sm text-xs"
                onClick={downloadDocxReport}
                disabled={downloadingReport}
                data-testid="preview-download-btn"
              >
                <Download className="h-3.5 w-3.5 mr-1.5" />
                Download DOCX
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="rounded-sm h-8 w-8 p-0"
                onClick={() => { setShowPreviewModal(false); setPreviewHtml(''); }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Preview body */}
          <div className="overflow-y-auto flex-1 bg-slate-100 p-6">
            {loadingPreview ? (
              <div className="flex flex-col items-center justify-center py-24 gap-4">
                <Loader2 className="h-10 w-10 animate-spin text-[#008080]" />
                <p className="text-slate-500 text-sm">Converting DOCX to HTML preview…</p>
              </div>
            ) : previewHtml ? (
              <div
                className="bg-white mx-auto shadow-md rounded-sm overflow-hidden"
                style={{ maxWidth: '820px', minHeight: '600px' }}
              >
                {/* Branding strip */}
                <div className="bg-[#003366] px-8 py-4 flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-widest">DPDP Readiness Assessment</p>
                    <p className="text-white font-[800] text-lg mt-0.5">{reportDetails?.org_profile?.org_name}</p>
                  </div>
                  <img src={LOGO_URL} alt="CodeXero" className="h-8 opacity-90" />
                </div>
                {/* Rendered HTML */}
                <div
                  className="px-10 py-8 report-preview-content"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewHtml) }}
                  data-testid="preview-html-content"
                />
                {/* Footer strip */}
                <div className="border-t border-slate-200 px-8 py-3 bg-slate-50">
                  <p className="text-xs text-slate-400 text-center">
                    Confidential — For informational purposes only. Does not constitute legal advice.
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-24 text-slate-500">No preview content available.</div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
